import { Extension } from "@tiptap/core";
import { AllSelection, TextSelection } from "@tiptap/pm/state";

const CLASS_PREFIX = "ql-indent-";

export const Indentation = Extension.create({
  name: "indent",

  addOptions() {
    return {
      types: ["heading", "listItem", "taskItem", "paragraph"],
      minLevel: 0,
      maxLevel: 8,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: null,
            parseHTML: (element) => {
              for (let i = 0; i < element.classList.length; i++) {
                const className = element.classList[i];
                if (className.startsWith(CLASS_PREFIX)) {
                  const level = parseInt(className.slice(CLASS_PREFIX.length), 10);
                  return level > this.options.minLevel && level <= this.options.maxLevel ? level : null;
                }
              }
              return null;
            },
            renderHTML: (attributes) => {
              if (
                !attributes.indent ||
                attributes.indent <= this.options.minLevel ||
                attributes.indent > this.options.maxLevel
              ) {
                return {};
              }
              return { class: CLASS_PREFIX + attributes.indent };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    const setNodeIndentMarkup = (tr, pos, delta) => {
      const node = tr?.doc?.nodeAt(pos);
      if (node) {
        const nextLevel = (node.attrs.indent || 0) + delta;
        const { minLevel, maxLevel } = this.options;
        let indent = nextLevel;
        if (nextLevel < minLevel) {
          indent = minLevel;
        } else if (nextLevel > maxLevel) {
          indent = maxLevel;
        }
        if (indent !== node.attrs.indent) {
          // eslint-disable-next-line
          const { indent: _, ...currentAttrs } = node.attrs;
          const nodeAttrs = indent > minLevel ? { ...currentAttrs, indent } : currentAttrs;
          return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
        }
      }
      return tr;
    }

    const updateIndentLevel = (tr, delta) => {
      const { doc, selection } = tr;
      if (
        doc &&
        selection &&
        (selection instanceof TextSelection || selection instanceof AllSelection)
      ) {
        const { from, to } = selection;
        doc.nodesBetween(from, to, (node, pos) => {
          if (this.options.types.includes(node.type.name)) {
            tr = setNodeIndentMarkup(tr, pos, delta);
            return false;
          }
          return true;
        });
      }
      return tr;
    }

    const applyIndent = (direction) => () => ({ tr, state, dispatch }) => {
      const { selection } = state;
      tr = tr.setSelection(selection);
      tr = updateIndentLevel(tr, direction);
      if (tr.docChanged) {
        dispatch?.(tr);
        return true;
      }
      return false;
    }
    return {
      indent: applyIndent(1),
      outdent: applyIndent(-1),
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        return this.editor.commands.indent();
      },
      'Shift-Tab': () => {
        return this.editor.commands.outdent();
      },
    };
  },
});
